import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { X } from 'lucide-react';
import { Alert, AlertDescription, AlertTitle } from '../../components/ui/alert';
import { Button } from '../../components/ui/button';
import { addTagToUserThunk } from '../settings/settingsSlice';
import type { RootState } from '../../reducers';

type P = () => boolean;
interface IDismissableBannerProps {
  id: string;
  title: string;
  body: string;
}
interface BannerConfig {
  id: string;
  title: string;
  body: string;
  predicate: P;
}
const DismissableBanner: React.FC<IDismissableBannerProps> = (props) => {
  const { id, title, body } = props;
  const dispatch = useDispatch();
  const userTags = useSelector((state: RootState) => state.settings.user.tags);

  const leaveMeAloneKey = `userAction_${id}`;
  const now = new Date();

  const lastDismissed = userTags[leaveMeAloneKey]
    ? new Date(userTags[leaveMeAloneKey])
    : null;
  let isWithin24Hours = false;
  if (lastDismissed) {
    const oneDayLater = new Date(lastDismissed);
    oneDayLater.setDate(oneDayLater.getDate() + 1);
    isWithin24Hours = now < oneDayLater;
  }

  if (isWithin24Hours) {
    return null;
  }

  const handleDismiss = () => {
    dispatch(
      addTagToUserThunk({
        key: leaveMeAloneKey,
        value: new Date().toISOString(),
      }),
    );
  };

  return (
    <Alert className="relative border-yellow-500/20 bg-yellow-50/50 dark:bg-yellow-900/10">
      <AlertTitle className="font-semibold">{title}</AlertTitle>
      <AlertDescription className="text-sm text-muted-foreground mt-1">
        {body}
      </AlertDescription>
      <Button
        variant="ghost"
        size="sm"
        className="absolute right-2 top-2 h-6 w-6 p-0"
        onClick={handleDismiss}
      >
        <X className="h-4 w-4" />
        <span className="sr-only">Dismiss</span>
      </Button>
    </Alert>
  );
};

const EncourageUserActionBanner: React.FC = () => {
  const { t } = useTranslation();
  const { user } = useSelector((state: RootState) => ({
    user: state?.settings.user,
  }));

  const { reconnect: reconnectGoogle } =
    user.integrations?.google?.calendar || {};
  const { reconnect: reconnectMicrosoft } =
    user.integrations?.microsoft?.calendar || {};
  const { reconnect: reconnectZoom } =
    user.integrations?.zoomPlatform?.generalApp || {};

  const config: BannerConfig[] = [
    {
      id: 'reconnect_google_calendar',
      body: t('reconnect.google.message'),
      title: t('reconnect.google.title'),
      predicate: () => Boolean(reconnectGoogle),
    },
    {
      id: 'reconnect_microsoft_calendar',
      body: t('reconnect.microsoft.message'),
      title: t('reconnect.microsoft.title'),
      predicate: () => Boolean(reconnectMicrosoft),
    },
    {
      id: 'reconnect_zoom_meetings',
      body: t('reconnect.zoom.message'),
      title: t('reconnect.zoom.title'),
      predicate: () => Boolean(reconnectZoom),
    },
  ].filter((c) => c.predicate());

  if (config.length === 0) return null;

  return (
    <>
      {config.map((banner) => {
        return (
          <DismissableBanner
            key={banner.id}
            id={banner.id}
            title={banner.title}
            body={banner.body}
          />
        );
      })}
    </>
  );
};
export default EncourageUserActionBanner;
